import React, { useEffect, useRef, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import { saveListName } from "../../http/api";
import { useListRfqStore } from "../../Store/listRfqStore";
import { useActualOrderStore } from "../../Store/actualOrderStore";
import useSnackbar from "../../Utils/useSnackbar";
import { tokens } from "../../Common/designTokens";

// Editing a list's label must not submit its read-only trading fields or create another RFQ.
const ListNameSaveButton = ({ token, listId, buttonSx, onSavingChange, onError, disabled = false }) => {
  const { listName, savedListName } = useListRfqStore((state) => state.listRfqData);
  const [saving, setSaving] = useState(false);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  const { showSuccess, SnackbarComponent } = useSnackbar();
  // Wait for the saved name before deciding whether the draft has changed.
  const dirty = savedListName !== undefined
    && (listName || '').trim() !== (savedListName || '').trim();

  const save = async () => {
    if (saving || disabled || !dirty) return;
    onError(null);
    setSaving(true);
    onSavingChange(true);
    // Capture the submitted draft: a later keystroke must not be overwritten by its response.
    const submittedName = listName;
    try {
      const result = await saveListName(token, listId, submittedName);
      if (!active.current) return;
      const name = result.listName || '';
      const store = useListRfqStore.getState();
      store.setListRfqData({ savedListName: name,
        ...(store.listRfqData.listName === submittedName ? { listName: name } : {}) });
      useActualOrderStore.getState().setActualOrderData({ listName: name });
      showSuccess("List name saved.");
    } catch (error) {
      if (active.current) onError(error?.message || "Unable to save list name.");
    } finally {
      if (active.current) setSaving(false);
      onSavingChange(false);
    }
  };

  return <>
    <Button variant="contained" onClick={save} disabled={disabled || saving || !dirty} sx={[buttonSx, {
      '&.Mui-disabled': {
        backgroundColor: tokens.colors.disabledBackground,
        color: tokens.colors.disabledFontColour,
        boxShadow: 'none',
      },
    }]}>
      {saving ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
    </Button>
    <SnackbarComponent />
  </>;
};

export default ListNameSaveButton;
